import { useState } from 'react'
import type { FormEvent } from 'react'
import SiteNav from '@/components/organisms/SiteNav'
import FooterStub from '@/components/organisms/FooterStub'
import SiteButton from '@/components/atoms/SiteButton'
import {trackFormSubmit, trackEvent} from '@/lib/analytics'
import { API_CONFIG } from '@/api/config'

const TOPICS = ['Sales and pricing', 'Press', 'Partnerships', 'Careers', 'Something else']

const FIELD_CLASSES =
    'w-full rounded-site-card border-2 border-unimo-site-grey-300 bg-white px-4 py-2 font-light text-unimo-slate disabled:cursor-not-allowed disabled:opacity-50'

// Simple inline form, not the old ContactForm modal — the field set here
// (name, email, category, message) doesn't match ContactForm's (name,
// company, email, phone), so it's a new component. What IS reused, per
// instruction, is the submission endpoint: the same CrossMoore Azure
// function ContactForm and BetaPartnerPage already post to
// (API_CONFIG.BETA_PARTNER_EMAIL_ENDPOINT) — no new backend.
export function ContactPage() {
    const [formData, setFormData] = useState({ name: '', email: '', topic: TOPICS[0], message: '' })
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setIsSubmitting(true)
        setError(null)

        try {
            // Category goes in the subject line, per instruction, so whoever
            // receives it knows what it's about without reading the message.
            const response = await fetch(API_CONFIG.BETA_PARTNER_EMAIL_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fromAddress: formData.email,
                    name: formData.name,
                    email: formData.email,
                    subject: `unimo website enquiry: ${formData.topic}`,
                    category: formData.topic,
                    message: formData.message,
                    formType: 'general-contact',
                }),
            })

            const data = await response.json()

            if (data.success) {
                setIsSubmitted(true)
                trackFormSubmit('general_contact', true)
                trackEvent('generate_lead', {form_name:'general_contact'})
            } else {
                throw new Error(data.message || 'Unknown error occurred')
            }
        } catch (err) {
            trackFormSubmit('general_contact', false)
            setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="min-h-screen bg-unimo-stone">
            <div className="site-shell">
                <SiteNav />

                <section className="pt-section-gap">
                    <h1 className="max-w-2xl text-4xl font-medium text-unimo-slate lg:text-5xl">Get in touch</h1>
                    <div className="mt-6 max-w-2xl space-y-4 font-light leading-relaxed text-unimo-slate">
                        <p>
                            Tell us what you&rsquo;re planning and we&rsquo;ll help you find the right products and pricing for your project.
                        </p>
                        <p>
                            For pricing, a digital twin for your event, partnerships, press, careers, or a general hello,
                            drop us a line using the form below and we&rsquo;ll get back to you.
                        </p>
                    </div>
                </section>

                <section className="max-w-2xl pb-section-gap pt-section-headline-gap-top">
                    {isSubmitted ? (
                        <p className="font-light text-unimo-slate">
                            Thanks - we&rsquo;ve got your message and will get back to you soon.
                        </p>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-unimo-slate">
                                    Name
                                </label>
                                <input
                                    id="contact-name"
                                    type="text"
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
                                    className={FIELD_CLASSES}
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div>
                                <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-unimo-slate">
                                    Email
                                </label>
                                <input
                                    id="contact-email"
                                    type="email"
                                    required
                                    value={formData.email}
                                    onChange={(e) => setFormData((f) => ({ ...f, email: e.target.value }))}
                                    className={FIELD_CLASSES}
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div>
                                <label htmlFor="contact-topic" className="mb-1.5 block text-sm font-medium text-unimo-slate">
                                    What&rsquo;s this about?
                                </label>
                                <select
                                    id="contact-topic"
                                    value={formData.topic}
                                    onChange={(e) => setFormData((f) => ({ ...f, topic: e.target.value }))}
                                    className={FIELD_CLASSES}
                                    disabled={isSubmitting}
                                >
                                    {TOPICS.map((topic) => (
                                        <option key={topic} value={topic}>
                                            {topic}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-unimo-slate">
                                    Message
                                </label>
                                <textarea
                                    id="contact-message"
                                    rows={5}
                                    required
                                    value={formData.message}
                                    onChange={(e) => setFormData((f) => ({ ...f, message: e.target.value }))}
                                    className={FIELD_CLASSES}
                                    disabled={isSubmitting}
                                />
                            </div>

                            {error && (
                                <p className="rounded-site-card border-2 border-unimo-site-grey-300 bg-white p-3 text-sm text-unimo-slate">
                                    {error}
                                </p>
                            )}

                            <SiteButton type="submit" disabled={isSubmitting} className="w-fit">
                                {isSubmitting ? 'Sending…' : 'Send message'}
                            </SiteButton>
                        </form>
                    )}
                </section>

                <FooterStub />
            </div>

        </div>
    )
}

export default ContactPage
